/**
 * useCredentialManagement Hook
 * Phase 1 Day 7 - Credential deletion management
 *
 * Manages credential deletion operations including modal interactions,
 * pending deletion tracking, and backend API calls for removing credentials
 * from devices.
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.selectedDeviceId - Currently selected device ID
 * @param {Function} params.authenticatedFetch - Authenticated fetch wrapper
 * @param {Set} params.pendingDeletionCredentials - Set of pending deletion credential keys
 * @param {Function} params.setPendingDeletionCredentials - Setter for pending deletions
 * @param {Function} params.setDevicesWithPendingDeletions - Setter for devices with pending deletions
 * @param {Function} params.invalidateDeviceStateCache - Cache invalidation function
 * @returns {Object} Credential management handlers
 */

import { useCallback } from "react";
import config from "../config";
import logger from "../utils/logger";
import { useNotification } from "./useNotification";
import { useModalManagement } from "./useModalManagement";

const COMMON_HEADERS = {
  "Content-Type": "application/json",
  "x-api-key": config.apiKey,
};

// Create composite key from deviceId and credentialId
const makePendingKey = (deviceId, credentialId) =>
  `${deviceId}:${credentialId}`;

export const useCredentialManagement = ({
  selectedDeviceId,
  authenticatedFetch,
  pendingDeletionCredentials,
  setPendingDeletionCredentials,
  setDevicesWithPendingDeletions,
  invalidateDeviceStateCache,
}) => {
  // Get notification functions
  const { showNotification } = useNotification();

  // Get modal management
  const {
    deleteCredentialModal,
    openDeleteCredentialModal,
    closeDeleteCredentialModal,
    setDeleteCredentialModalDeleting,
  } = useModalManagement();

  /**
   * Open delete credential modal with patron information
   * @param {string} credentialId - Credential ID to delete
   * @param {Object} patron - Patron object with name/email
   */
  const handleDeleteCredentialFromDevice = useCallback(
    (credentialId, patron) => {
      const patronName = patron
        ? `${patron.firstName || ""} ${patron.lastName || ""}`.trim() ||
          patron.email
        : null;

      openDeleteCredentialModal(credentialId, patronName);
    },
    [openDeleteCredentialModal]
  );

  /**
   * Confirm and execute credential deletion
   */
  const handleConfirmDeleteCredential = useCallback(async () => {
    const { credentialId } = deleteCredentialModal;
    const deviceId = selectedDeviceId;

    if (!credentialId || !deviceId) {
      logger.error("Missing credentialId or deviceId for deletion");
      return;
    }

    setDeleteCredentialModalDeleting(true);

    try {
      logger.log(`Removing credential ${credentialId} from device ${deviceId}`);

      const response = await authenticatedFetch(
        `${config.apiEndpoint}/credentials/${credentialId}/devices/${deviceId}`,
        {
          method: "DELETE",
          headers: COMMON_HEADERS,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Failed to remove credential: ${response.status}`
        );
      }

      const data = await response.json();
      logger.log("Credential removal queued:", data);

      // Invalidate cache after credential deletion
      invalidateDeviceStateCache(deviceId);

      // Add device-credential pair to pending deletion set
      const pendingKey = makePendingKey(deviceId, credentialId);
      logger.log(`[handleConfirmDeleteCredential] 🚀 DELETION INITIATED:`);
      logger.log(`[handleConfirmDeleteCredential] - pendingKey: ${pendingKey}`);
      logger.log(`[handleConfirmDeleteCredential] - deviceId: ${deviceId}`);
      logger.log(
        `[handleConfirmDeleteCredential] - credentialId: ${credentialId}`
      );

      setPendingDeletionCredentials((prev) => {
        const updated = new Set(prev).add(pendingKey);
        logger.log(
          `[handleConfirmDeleteCredential] - pendingDeletionCredentials AFTER add:`,
          Array.from(updated)
        );
        return updated;
      });

      // Mark this device as having pending deletions (disables all delete buttons on this device)
      setDevicesWithPendingDeletions((prev) => {
        const updated = new Set(prev).add(deviceId);
        logger.log(
          `[handleConfirmDeleteCredential] - devicesWithPendingDeletions AFTER add:`,
          Array.from(updated)
        );
        return updated;
      });

      showNotification({
        message: "Credential removal initiated. Processing...",
        type: "success",
      });

      // Close the modal
      closeDeleteCredentialModal();
    } catch (error) {
      logger.error("Error removing credential from device:", error);
      showNotification({
        message: `Failed to remove credential: ${error.message}`,
        type: "error",
      });
      setDeleteCredentialModalDeleting(false);
    }
  }, [
    deleteCredentialModal,
    selectedDeviceId,
    authenticatedFetch,
    invalidateDeviceStateCache,
    setPendingDeletionCredentials,
    setDevicesWithPendingDeletions,
    showNotification,
    closeDeleteCredentialModal,
    setDeleteCredentialModalDeleting,
  ]);

  /**
   * Cancel credential deletion (close modal)
   */
  const handleCancelDeleteCredential = useCallback(() => {
    closeDeleteCredentialModal();
  }, [closeDeleteCredentialModal]);

  return {
    // Actions
    handleDeleteCredentialFromDevice,
    handleConfirmDeleteCredential,
    handleCancelDeleteCredential,

    // State (for backward compatibility)
    deleteCredentialModal,
  };
};
